/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { ClinicProfileSettingsResponse } from "../../../../models/clinic/profile/ClinicProfileSettingsResponse";
import { useForm } from "react-hook-form";
import { getStringFromEnumsOrReturn, strings } from "../../../../common/Strings/Strings";
import ClinicProfileApi from "../../../../api/ClinicProfileApi";
import { canManageClinic, getGeneralError } from "../../../../util/helperFunctions";
import Select from "../../../../components/ReactHookFormFields/General/Select/Select";
import { clinicProfileVisibilityOptions } from "../ClinicProfileSettings";
import AlertBox, { AlertType } from "../../../../components/AlertBox";
import Button from "../../../../components/Button";
import CheckBox from "../../../../components/ReactHookFormFields/General/CheckBox";
import { EmployeesRequest } from "../../../../models/clinic/profile/EmployeesRequest";
import { ClinicEmploymentType } from "../../../../models/employment/ClinicEmploymentType";
import { ClinicResponse } from "../../../../models/clinic/ClinicResponse";

interface Props {
  clinic: ClinicResponse;
  settings: ClinicProfileSettingsResponse;
  setSettings: (settings: ClinicProfileSettingsResponse) => void;
}

const Employees: React.FC<Props> = (props: Props) => {
  const { clinic, settings, setSettings } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<EmployeesRequest>({
    mode: "onSubmit",
    defaultValues: settings,
  });

  useEffect(() => {
    reset(settings);
  }, [settings]);

  const saveSettings = async (request: EmployeesRequest) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response = await ClinicProfileApi.updateEmployees(clinic.id, request);
      setSettings(response.data);
      setSuccess(true);
    } catch (e) {
      setError(await getGeneralError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(saveSettings)}>
      <div className="tw-card divide-y divide-gray-200 dark:divide-gray-700">
        <div className="p-4 flex items-center">
          <p className="uppercase">{strings.clinicProfileColleagues}</p>
        </div>
        <div className="p-4 space-y-4">
          <Select
            label={strings.employeesVisibility}
            name="employeesVisibility"
            register={register}
            options={clinicProfileVisibilityOptions}
            error={errors.employeesVisibility}
            readOnly={!canManageClinic(clinic)}
          />
          <div>
            <label htmlFor="displayedEmployees">{strings.visibleEmployees}</label>
            <div className="space-y-2">
              {Object.keys(ClinicEmploymentType).map((type) => (
                <CheckBox
                  name={"displayedEmployees"}
                  id={type}
                  register={register}
                  value={type}
                  label={getStringFromEnumsOrReturn(type)}
                  readOnly={!canManageClinic(clinic)}
                />
              ))}
            </div>
          </div>
          <AlertBox message={error} />
          <AlertBox closeAble={false} hidden={!success} message={strings.savedSuccessfully} type={AlertType.SUCCESS} />
          <div className="text-right">
            <Button
              disabled={loading || !isDirty}
              loading={loading}
              type="submit"
              fullWidth={false}
              hidden={!canManageClinic(clinic)}
            >
              {strings.save}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default Employees;
